<template>
  <div :style="`height: ${height}`" class="activity-feed">
    <v-progress-linear
      v-if="isLoading"
      color="border"
      indeterminate
      data-testid="linear-progress-bar"
    ></v-progress-linear>
    <template v-else>
      <div ref="activity-feed" class="activity-feed--overflow">
        <SoAlert
          v-if="!pageItems.length"
          title="No results"
          data-testid="activity-feed-alert"
        />
        <div
          v-for="item in pageItems"
          v-else
          :key="item.date"
          data-testid="tickets-grouped-by-date"
          class="mr-4"
        >
          <SoFeed :date="item.date">
            <template v-for="event in item.items" :key="event.feedKey">
              <SoFeedItem v-bind="{ ...event.props }">
                <div data-testid="feed-item">
                  <SoSimpleTable
                    v-if="event.info"
                    :headers="event.info.headers"
                    :items="[event.info.row]"
                    :copy-to-clipboard-fields="event.info.copyFields"
                    class="py-2"
                    is-pivoted
                    outlined
                    :show-copy-to-clipboard="!event.info.copyFields?.length"
                    @click:row="$emit('click:table-row', event.item)"
                  >
                    <template v-if="event.info.path && !disableRoutes" #actions>
                      <SoTooltip :text="`Go to ${event.props.title}`">
                        <SoButton
                          icon="external-link"
                          type="icon"
                          data-testid="activity-feed-path-btn"
                          @click="goTo(event.info.path)"
                        />
                      </SoTooltip>
                    </template>

                    <template #tableContent>
                      <slot name="tableContent" :content="event.item"> </slot>
                    </template>
                  </SoSimpleTable>

                  <SoWidget v-if="event.html">
                    <div v-html="event.html" />
                  </SoWidget>
                  <ActivityFeedFetchedEmailContent
                    v-else-if="event.htmlPath"
                    :path="event.htmlPath"
                  />
                  <ActivityFeedJuniferCommContent
                    v-else-if="isJuniferCommFeedItem(event)"
                    :comm="event.item"
                  />
                  <div v-else-if="isFreshdeskTicket(event.item)">
                    <FreshdeskMessageThread
                      :conversation="getMessageThread(event)"
                    />
                  </div>
                </div>
              </SoFeedItem>
            </template>
          </SoFeed>
        </div>
      </div>
      <SoPagination
        v-show="feedItemList.length > pageSize"
        :key="paginationResetKey"
        v-model="pageItems"
        :list="feedItemList"
        :page-size="pageSize"
        class="mt-8"
        @update:page="scrollTop()"
      />
    </template>
  </div>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import SoFeed from "@/components/feed/SoFeed.vue"
  import SoPagination from "@/components/SoPagination.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoFeedItem from "@/components/feed/SoFeedItem.vue"
  import SoSimpleTable from "@/components/SoSimpleTable.vue"
  import SoTooltip from "@/components/SoTooltip.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoWidget from "@/components/SoWidget.vue"
  import ActivityFeedFetchedEmailContent from "./ActivityFeedFetchedEmailContent.vue"
  import ActivityFeedJuniferCommContent from "./ActivityFeedJuniferCommContent.vue"
  import {
    ActivityFeedItemType,
    IItemsByDate,
    JuniferCommFeedItem,
  } from "@/models/activityFeed/ActivityFeedItem"
  import { daysFromTodayFormatted } from "@/helpers/dateHelper"
  import FreshdeskMessageThread from "../../complaints/components/FreshdeskMessageThread.vue"
  import { IFreshdeskTicket } from "@/models/freshdeskTicket/freshdeskTicket.types"
  import { FreshdeskTicket } from "@/models/freshdeskTicket/freshdeskTicket"

  export default defineComponent({
    components: {
      SoFeed,
      SoPagination,
      SoAlert,
      SoFeedItem,
      SoSimpleTable,
      SoTooltip,
      SoButton,
      SoWidget,
      ActivityFeedFetchedEmailContent,
      ActivityFeedJuniferCommContent,
      FreshdeskMessageThread,
    },
    props: {
      feedItemList: {
        type: Array as PropType<IItemsByDate[]>,
        default: () => [],
        required: true,
      },
      height: {
        type: String,
        default: "600px",
      },
      disableRoutes: {
        type: Boolean,
        default: false,
      },
      isLoading: {
        type: Boolean,
        default: false,
      },
      // Changing it sends the agent back to page 1, e.g. on a filter change.
      // Items arriving or leaving otherwise keep the current page.
      paginationResetKey: {
        type: String,
        default: "",
      },
    },
    emits: ["click:table-row"],
    data() {
      return {
        pageSize: 8,
        pageItems: [] as IItemsByDate[],
        daysFromTodayFormatted: daysFromTodayFormatted,
      }
    },
    methods: {
      scrollTop(): void {
        const activityFeed = this.$refs["activity-feed"] as HTMLElement
        if (activityFeed) activityFeed.scrollTop = 0
      },
      goTo(path: string) {
        if (path.match(/^https?:\/\//)) {
          window.open(path, "_blank")
        } else {
          this.$router.push({ name: path })
        }
      },
      getMessageThread(event) {
        return (event.item as IFreshdeskTicket)?.thread
      },
      isFreshdeskTicket(item: ActivityFeedItemType | {}) {
        return item instanceof FreshdeskTicket
      },
      isJuniferCommFeedItem(
        event: ActivityFeedItemType,
      ): event is JuniferCommFeedItem {
        return event instanceof JuniferCommFeedItem
      },
    },
  })
</script>

<style scoped>
  .activity-feed {
    display: flex;
    flex-direction: column;
    align-items: stretch;
  }
  .activity-feed--overflow {
    flex-grow: 1;
    overflow-y: scroll;
  }
</style>
